/// <reference path="../../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// In-chat Coin Flip tile. Realtime, animated, plays between two users:
//   • creator calls heads/tails (move 0)
//   • opponent taps Flip (move 1 — their client generates the result)
//   • both clients animate the coin and show the same winner.

import { Coins, type LucideIcon } from 'lucide-react-native';
import { useEffect, useRef } from 'react';
import { Animated, Easing, Pressable, Text, View } from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { tokens } from '../../design-system/tokens';
import { useTileMaxWidth } from '../tileConstraint';

// Loose color shape shared by all game tiles — the light/dark palettes
// have divergent string-literal types, so we widen the fields we read to
// `string` to accept either palette.
export type GameColors = {
  readonly surface: { readonly tertiary: string };
  readonly text: { readonly primary: string; readonly secondary: string; readonly tertiary: string };
  readonly border: { readonly secondary: string };
  readonly accent: { readonly indigo: string };
};
import {
  type CoinSide,
  encodeCoinFlip,
  encodeCoinPick,
  hasMoved,
  randomCoin,
  readCoinState,
} from './gameLogic';
import type { GameSession } from '../../sync/useGameSessions';

export interface CoinFlipTileProps {
  readonly session: GameSession;
  readonly isCreator: boolean;
  /** Tile sits on the indigo self-bubble fill → use white chrome. */
  readonly onBrand?: boolean | undefined;
  readonly onMove: (moveIdx: number, payload: string) => void;
  readonly selfName?: string | undefined;
  readonly opponentName?: string | undefined;
}

export function CoinFlipTile({
  session,
  isCreator,
  onBrand,
  onMove,
  selfName,
  opponentName,
}: CoinFlipTileProps): JSX.Element {
  const scheme = useColorScheme();
  const c: GameColors = tokens.color.semantic[scheme === 'dark' ? 'dark' : 'light'];
  // Thread side panel: cap the tile to the provided width.
  const tileMax = useTileMaxWidth();
  // On the brand fill the indigo accent + tertiary text vanish — swap to
  // white chrome so the game stays legible.
  const accent = onBrand ? '#FFFFFF' : c.accent.indigo;
  const subText = onBrand ? 'rgba(255,255,255,0.75)' : undefined;
  const state = readCoinState(session);

  // Spin animation — runs once when the flip result lands.
  const spin = useRef(new Animated.Value(0)).current;
  const spunFor = useRef<string | null>(null);
  useEffect(() => {
    if (state.result !== null && spunFor.current !== session.sessionId + state.result) {
      spunFor.current = session.sessionId + state.result;
      spin.setValue(0);
      Animated.timing(spin, {
        toValue: 1,
        duration: 1100,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }).start();
    }
  }, [state.result, session.sessionId, spin]);

  const rotateX = spin.interpolate({
    inputRange: [0, 1],
    // 5 full turns; the final face is set by `faceSide` below.
    outputRange: ['0deg', '1800deg'],
  });

  const creatorPicked = hasMoved(session, 0);
  const flipped = hasMoved(session, 1);
  const iAmCreator = isCreator;
  const myTurnToPick = iAmCreator && !creatorPicked;
  const myTurnToFlip = !iAmCreator && creatorPicked && !flipped;

  const faceSide: CoinSide = state.result ?? state.side ?? 'heads';

  const pick = (side: CoinSide): void => onMove(0, encodeCoinPick(side));
  const flip = (): void => onMove(1, encodeCoinFlip(randomCoin()));

  const iWon = state.resolved && state.winnerSeat === (iAmCreator ? 0 : 1);
  const winnerName = state.winnerSeat === 0 ? (iAmCreator ? selfName ?? 'You' : opponentName ?? 'They') : iAmCreator ? opponentName ?? 'They' : selfName ?? 'You';

  return (
    <View
      className={`w-[280px] max-w-full rounded-[18px] p-md gap-sm border ${
        onBrand
          ? 'bg-light-accent-brand dark:bg-[#537EFF] border-white/10'
          : 'bg-light-surface-secondary dark:bg-[#1A1D24] border-light-border-secondary dark:border-white/[0.06]'
      }`}
      style={tileMax !== null ? { maxWidth: tileMax } : undefined}
      accessibilityLabel="Coin flip game"
    >
      <Header title="Coin Flip" Icon={Coins} colorAccent={accent} />

      {/* Coin */}
      <View className="items-center justify-center py-sm">
        <Animated.View
          style={{
            width: 72,
            height: 72,
            borderRadius: 36,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#F5C542',
            borderWidth: 3,
            borderColor: '#D9A520',
            transform: [{ perspective: 400 }, { rotateX }],
          }}
        >
          <Text style={{ fontSize: 30, fontWeight: '800', color: '#7A5B00' }}>
            {faceSide === 'heads' ? 'H' : 'T'}
          </Text>
        </Animated.View>
        {state.side !== null ? (
          <Text
            className="font-sans text-[11px] leading-4 pt-1 text-light-text-tertiary dark:text-[#B5BAC3]"
            style={subText !== undefined ? { color: subText } : undefined}
          >
            {(iAmCreator ? selfName ?? 'You' : opponentName ?? 'Opponent')} called {state.side}
          </Text>
        ) : null}
      </View>

      {/* Action / status */}
      {state.resolved ? (
        <Banner
          text={state.winnerSeat === -1 ? 'Draw' : `${winnerName} won!`}
          win={iWon}
          colors={c}
        />
      ) : myTurnToPick ? (
        <View className="flex-row gap-sm">
          <ChoiceButton label="Heads" onPress={() => pick('heads')} accent={accent} />
          <ChoiceButton label="Tails" onPress={() => pick('tails')} accent={accent} />
        </View>
      ) : myTurnToFlip ? (
        <PrimaryButton
          label="Flip the coin!"
          onPress={flip}
          accent={accent}
          {...(onBrand ? { textColor: c.accent.indigo } : {})}
        />
      ) : (
        <WaitingRow
          text={!creatorPicked ? 'Waiting for the call…' : 'Waiting for the flip…'}
          color={subText ?? c.text.tertiary}
        />
      )}
    </View>
  );
}

// ── shared sub-components (used by all game tiles) ──────────────────────

export function Header({
  title,
  Icon,
  colorAccent,
}: {
  title: string;
  Icon: LucideIcon;
  colorAccent: string;
}): JSX.Element {
  return (
    <View className="flex-row items-center gap-xs">
      <Icon size={16} color={colorAccent} />
      <Text
        className="font-sans font-bold text-[14px] leading-5 text-light-text-primary dark:text-white"
        style={{ color: colorAccent }}
      >
        {title}
      </Text>
    </View>
  );
}

export function ChoiceButton({
  label,
  onPress,
  accent,
}: {
  label: string;
  onPress: () => void;
  accent: string;
}): JSX.Element {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      onPress={onPress}
      className="flex-1 h-[36px] rounded-[10px] items-center justify-center bg-light-surface-tertiary dark:bg-white/[0.06] active:opacity-70"
      style={{ borderWidth: 1, borderColor: accent }}
    >
      <Text className="font-sans font-semibold text-[13px]" style={{ color: accent }}>
        {label}
      </Text>
    </Pressable>
  );
}

export function PrimaryButton({
  label,
  onPress,
  accent,
  textColor,
}: {
  label: string;
  onPress: () => void;
  accent: string;
  /** Override the label color (e.g. indigo on a white-on-brand button). */
  textColor?: string | undefined;
}): JSX.Element {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      onPress={onPress}
      className="h-[38px] rounded-[10px] items-center justify-center active:opacity-80"
      style={{ backgroundColor: accent }}
    >
      <Text
        className="font-sans font-bold text-[13px] text-white"
        style={textColor !== undefined ? { color: textColor } : undefined}
      >
        {label}
      </Text>
    </Pressable>
  );
}

export function WaitingRow({ text, color }: { text: string; color: string }): JSX.Element {
  return (
    <View className="h-[34px] items-center justify-center">
      <Text className="font-sans text-[12px] leading-4" style={{ color }}>
        {text}
      </Text>
    </View>
  );
}

export function Banner({
  text,
  win,
  colors,
}: {
  text: string;
  win: boolean;
  colors: GameColors;
}): JSX.Element {
  const bg = win ? '#3CCB4F22' : colors.surface.tertiary;
  const fg = win ? '#3CCB4F' : colors.text.primary;
  return (
    <View
      className="h-[38px] rounded-[10px] items-center justify-center"
      style={{ backgroundColor: bg }}
    >
      <Text className="font-sans font-bold text-[14px]" style={{ color: fg }}>
        {win ? '🎉 ' : ''}
        {text}
      </Text>
    </View>
  );
}
